import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAppDispatch } from "@/app/hooks";
import { cancelEventSessionAction } from "../../slices/eventSessionListSlice";

interface CancelEventSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  sessionUuid: string | null;
}

function CancelEventSessionModal({
  isOpen,
  onClose,
  onSuccess,
  sessionUuid,
}: CancelEventSessionModalProps) {
  const dispatch = useAppDispatch();
  const [isCancelling, setIsCancelling] = useState(false);
  const [sendNotification, setSendNotification] = useState(true);

  const handleCancel = async () => {
    if (!sessionUuid) return;
    setIsCancelling(true);
    try {
      await dispatch(cancelEventSessionAction(sessionUuid, sendNotification));
      onSuccess();
    } catch (error) {
      console.error("Failed to cancel session:", error);
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Cancel Session</DialogTitle>
          <DialogDescription>
            Are you sure you want to cancel this session? Registration and
            payment data will be preserved. Refunds can be issued manually from
            the billing tab.
          </DialogDescription>
        </DialogHeader>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            checked={sendNotification}
            className="h-4 w-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
            onChange={(e) => setSendNotification(e.target.checked)}
            type="checkbox"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Send cancellation notification to enrolled users
          </span>
        </label>

        <DialogFooter>
          <Button disabled={isCancelling} onClick={onClose} variant="outline">
            Go Back
          </Button>
          <Button
            disabled={isCancelling || !sessionUuid}
            onClick={handleCancel}
            variant="destructive"
          >
            {isCancelling ? "Cancelling..." : "Cancel Session"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CancelEventSessionModal;
